import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Search,
  Filter,
  Calendar,
  User,
  Edit,
  ChevronDown,
  DollarSign,
  AlertCircle,
  CheckCircle,
  Clock,
} from "lucide-react";
import { format } from "date-fns";
import medicalDB from "../services/database";

const PatientList = () => {
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const [filters, setFilters] = useState({
    search: searchParams.get("search") || "",
    status: searchParams.get("status") || "",
    sortBy: searchParams.get("sortBy") || "updated",
  });
  const [searchTerm, setSearchTerm] = useState(
    searchParams.get("search") || ""
  );
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    loadPatients();
  }, [filters]);

  const loadPatients = async () => {
    try {
      setLoading(true);
      const data = await medicalDB.getAllPatients(filters);

      // Load financial data for each patient
      const patientsWithFinancials = await Promise.all(
        data.map(async (patient) => {
          try {
            const balance = await medicalDB.getPatientBalance(patient.id);
            return {
              ...patient,
              financialSummary: balance,
            };
          } catch (error) {
            console.error(
              `Error loading financial data for patient ${patient.id}:`,
              error
            );
            return {
              ...patient,
              financialSummary: {
                totalOwed: 0,
                totalPaid: 0,
                balance: 0,
                transactions: 0,
              },
            };
          }
        })
      );

      setPatients(patientsWithFinancials);
    } catch (error) {
      console.error("Error loading patients:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);

    // Update URL search params
    const newSearchParams = new URLSearchParams();
    Object.entries(newFilters).forEach(([k, v]) => {
      if (v) newSearchParams.set(k, v);
    });
    setSearchParams(newSearchParams);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "Under Observation":
        return "status-under-observation";
      case "Follow-Up":
        return "status-follow-up";
      case "Discharged":
        return "status-discharged";
      default:
        return "bg-gray-500 text-white";
    }
  };

  const getPaymentStatusIcon = (balance) => {
    if (balance > 0) {
      return <AlertCircle className="h-4 w-4 text-red-500" />;
    } else if (balance === 0) {
      return <CheckCircle className="h-4 w-4 text-green-500" />;
    } else {
      return <DollarSign className="h-4 w-4 text-gray-400" />;
    }
  };

  const getPaymentStatusColor = (balance) => {
    if (balance > 0) {
      return "text-red-600 bg-red-50 border-red-200";
    } else if (balance === 0) {
      return "text-green-600 bg-green-50 border-green-200";
    } else {
      return "text-gray-600 bg-gray-50 border-gray-200";
    }
  };

  const getPaymentStatusText = (balance) => {
    if (balance > 0) {
      return `₪${balance.toFixed(2)} Due`;
    } else if (balance === 0) {
      return "Paid Up";
    } else {
      return "No Bills";
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    handleFilterChange("search", searchTerm);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-gray-600">Loading patients...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="patient-list">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Patients</h1>
        <div className="mt-4 sm:mt-0 flex items-center space-x-3">
          <span className="text-sm text-gray-600">
            {patients.length} patients
          </span>
          <Link
            to="/patients/new"
            className="btn-primary"
            data-agent-action="add-patient"
          >
            Add New Patient
          </Link>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="card">
        <form
          onSubmit={handleSearch}
          className="flex flex-col sm:flex-row gap-4"
        >
          <div className="flex-1 flex gap-2">
            <div className="relative flex-1">
              <Search
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                size={20}
              />
              <input
                type="text"
                placeholder="Search by name, complaint, or medical history..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="form-input pl-10"
                data-agent-action="search-patients"
                data-testid="patient-search"
              />
            </div>
            <button
              type="submit"
              className="btn-primary flex items-center space-x-2"
            >
              <Search size={16} />
              <span className="hidden sm:inline">Search</span>
            </button>
          </div>

          <button
            type="button"
            onClick={() => setShowFilters(!showFilters)}
            className="btn-secondary flex items-center space-x-2"
          >
            <Filter size={20} />
            <span>Filters</span>
            <ChevronDown
              size={16}
              className={`transition-transform ${
                showFilters ? "rotate-180" : ""
              }`}
            />
          </button>
        </form>

        {/* Advanced Filters */}
        {showFilters && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <label className="form-label">Status</label>
                <select
                  value={filters.status}
                  onChange={(e) => handleFilterChange("status", e.target.value)}
                  className="form-input"
                >
                  <option value="">All Statuses</option>
                  <option value="Under Observation">Under Observation</option>
                  <option value="Follow-Up">Follow-Up</option>
                  <option value="Discharged">Discharged</option>
                </select>
              </div>

              <div>
                <label className="form-label">Sort By</label>
                <select
                  value={filters.sortBy}
                  onChange={(e) => handleFilterChange("sortBy", e.target.value)}
                  className="form-input"
                >
                  <option value="updated">Last Updated</option>
                  <option value="name">Name</option>
                  <option value="age">Age</option>
                </select>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Patient List */}
      {patients.length === 0 ? (
        <div className="card text-center py-12">
          <User className="mx-auto h-12 w-12 text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No patients found
          </h3>
          <p className="text-gray-600 mb-6">
            {filters.search || filters.status
              ? "Try adjusting your search or filters"
              : "Get started by adding your first patient"}
          </p>
          <Link
            to="/patients/new"
            className="btn-primary"
            data-agent-action="add-patient"
          >
            Add First Patient
          </Link>
        </div>
      ) : (
        <div className="grid gap-4">
          {patients.map((patient) => (
            <div
              key={patient.id}
              onClick={() => (window.location.href = `/patients/${patient.id}`)}
              className="card hover:shadow-lg transition-shadow cursor-pointer group"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-medical-lightblue rounded-full flex items-center justify-center group-hover:bg-medical-blue transition-colors">
                    <User className="h-6 w-6 text-medical-blue group-hover:text-white transition-colors" />
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 group-hover:text-medical-blue transition-colors">
                      {patient.name}
                    </h3>
                    <div className="text-sm text-gray-600 space-x-4">
                      <span>{patient.age} years old</span>
                      <span>{patient.gender}</span>
                      <span>{patient.visitCount || 0} visits</span>
                    </div>

                    <div className="flex items-center space-x-4 mt-1">
                      {patient.lastVisit && (
                        <div className="text-sm text-gray-500">
                          Last visit:{" "}
                          {format(
                            new Date(patient.lastVisit.date),
                            "MMM dd, yyyy"
                          )}
                        </div>
                      )}

                      {patient.nextAppointment && (
                        <div className="flex items-center space-x-1 text-sm">
                          <Calendar size={14} className="text-medical-blue" />
                          <span className="text-medical-blue font-medium">
                            Next:{" "}
                            {format(
                              new Date(patient.nextAppointment.date),
                              "MMM dd"
                            )}{" "}
                            at {patient.nextAppointment.startTime}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex items-center space-x-3">
                  <span
                    className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(
                      patient.status
                    )}`}
                  >
                    {patient.status}
                  </span>

                  {/* Payment Status Indicator */}
                  {patient.financialSummary && (
                    <span
                      className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border ${getPaymentStatusColor(
                        patient.financialSummary.balance
                      )}`}
                      title={`Total owed: ₪${patient.financialSummary.totalOwed.toFixed(
                        2
                      )}, Paid: ₪${patient.financialSummary.totalPaid.toFixed(
                        2
                      )}`}
                    >
                      {getPaymentStatusIcon(patient.financialSummary.balance)}
                      {getPaymentStatusText(patient.financialSummary.balance)}
                    </span>
                  )}

                  <Link
                    to={`/appointments/new?patientId=${patient.id}`}
                    className="p-2 text-gray-400 hover:text-green-600 transition-colors z-10 relative"
                    title="Schedule Appointment"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Calendar size={20} />
                  </Link>

                  <Link
                    to={`/patients/${patient.id}/edit`}
                    className="p-2 text-gray-400 hover:text-medical-blue transition-colors z-10 relative"
                    title="Edit Patient"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Edit size={20} />
                  </Link>
                </div>
              </div>

              {/* Recent Visit Preview */}
              {patient.lastVisit && (
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <div className="text-sm">
                    <span className="font-medium text-gray-700">
                      Recent complaints:{" "}
                    </span>
                    <span className="text-gray-600">
                      {patient.lastVisit.complaints?.slice(0, 3).join(", ") ||
                        "None recorded"}
                    </span>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PatientList;
